import { useLayoutEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { setViewport } from './utils/viewports';
import { installDebugLogCapture } from './utils/debugLog';
import './utils/pwaRegistration';
import { installNativeFetchBridge } from './utils/platform';
import {
  isIgnorableGlobalWindowErrorMessage,
  serializeUnhandledRejectionReason,
} from './utils/browserErrors';

installDebugLogCapture();
installNativeFetchBridge();
import { googleFonts, generateGoogleFontsLink } from './fonts.config';
import { logger } from './utils/logger';

const bootstrapProbe =
  typeof window !== 'undefined'
      ? (window as Window & {
        __wereaderBootstrapProbe?: {
          record: (type: string, details?: Record<string, unknown>) => void;
          flush?: () => void;
          markAppMounted?: () => void;
        };
      }).__wereaderBootstrapProbe
    : undefined;

bootstrapProbe?.record('main-module-evaluated');

logger.info('Bootstrapping app entrypoint', {
  href: window.location.href,
  userAgent: navigator.userAgent,
});

window.addEventListener('error', (event) => {
  const details = {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error instanceof Error
      ? {
          name: event.error.name,
          message: event.error.message,
          stack: event.error.stack,
        }
      : event.error,
  };

  if (isIgnorableGlobalWindowErrorMessage(event.message)) {
    logger.debug('Ignoring benign global window error', details);
    event.preventDefault();
    return;
  }

  logger.error('Global window error', details);
});

window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled promise rejection', {
    reason: serializeUnhandledRejectionReason(event.reason),
  });
});

window.addEventListener('vite:preloadError', () => {
  logger.warn('Vite preload error encountered, forcing reload');
  window.location.reload();
});

logger.debug('Applying viewport configuration');
bootstrapProbe?.record('before-setViewport');
setViewport();

// Add fonts before rendering
logger.debug('Injecting Google Fonts link set');
bootstrapProbe?.record('before-font-injection', {
  fontCount: googleFonts.length,
});
document.head.insertAdjacentHTML('beforeend', generateGoogleFontsLink(googleFonts));

const rootElement = document.getElementById('root');
logger.debug('Resolved root element', {
  exists: Boolean(rootElement),
});
bootstrapProbe?.record('root-element-resolved', {
  exists: Boolean(rootElement),
});

if (!rootElement) {
  bootstrapProbe?.record('root-element-missing');
  bootstrapProbe?.flush?.();
  throw new Error('App root element #root was not found');
}

logger.info('Rendering React root');
bootstrapProbe?.record('before-react-render');

const BootstrapLifecycleBoundary = () => {
  useLayoutEffect(() => {
    bootstrapProbe?.markAppMounted?.();
    logger.info('Bootstrap probe disarmed after initial mount');
  }, []);

  return <App />;
};

ReactDOM.createRoot(rootElement as HTMLElement).render(<BootstrapLifecycleBoundary />);
bootstrapProbe?.record('after-react-render-called');
bootstrapProbe?.flush?.();
